import { Inject, Injectable } from '@angular/core';
import { DcLocalStorage, WINDOW } from '@datachain/ui-sdk/common';
import { immerable, produce } from 'immer';

import { DcCoreElementType, DcCoreModule } from '../dc-core-types';

interface IProjectStorage {
	projectId: number;
	projectLabel: string;
	projectVersion: string;
	selectedContext: number;
	isJobsCacheActive: boolean;
	projectsSearchTerm: string;
	projectsViewMode: string;
	selectedElement: DcCoreElementType;
	module: DcCoreModule;
	isDefaultVersion: boolean;
	versionCount: number;
}

const entityDefaults: IProjectStorage = {
	projectId: -1,
	projectLabel: 'no project',
	projectVersion: 'V1',
	isDefaultVersion: false,
	selectedContext: -1,
	isJobsCacheActive: true,
	projectsSearchTerm: '',
	projectsViewMode: 'CARD',
	selectedElement: DcCoreElementType.UNKNOWN,
	module: 'generics',
	versionCount: 0,
};

class UserSettings implements IProjectStorage {
	private [immerable] = true;
	public projectId = entityDefaults.projectId;
	public projectLabel = entityDefaults.projectLabel;
	public projectVersion = entityDefaults.projectVersion;
	public isDefaultVersion = entityDefaults.isDefaultVersion;
	public versionCount = entityDefaults.versionCount;
	public selectedContext = entityDefaults.selectedContext;
	public isJobsCacheActive = entityDefaults.isJobsCacheActive;
	public projectsSearchTerm = entityDefaults.projectsSearchTerm;
	public projectsViewMode = entityDefaults.projectsViewMode;
	public selectedElement = entityDefaults.selectedElement;
	public module = entityDefaults.module;

	public log(settings: UserSettings): void {
		// console.warn(
		// 	'%c [User Settings]:',
		// 	'color: #4CAF50; background-color: black; font-weight: bold',
		// 	JSON.stringify(settings)
		// );
	}

	public setProjectId(id: number): UserSettings {
		const updated = produce(this, (draft: UserSettings) => {
			draft.projectId = id;
		});
		this.log(updated);
		return updated;
	}

	public setProjectLabel(label: string): UserSettings {
		const updated = produce(this, (draft: UserSettings) => {
			draft.projectLabel = label;
		});
		this.log(updated);
		return updated;
	}

	public setProjectVersion(version: string): UserSettings {
		const updated = produce(this, (draft: UserSettings) => {
			draft.projectVersion = version;
		});
		this.log(updated);
		return updated;
	}

	public setIsDefaultVersion(isDefaultVersion: boolean): UserSettings {
		const updated = produce(this, (draft: UserSettings) => {
			draft.isDefaultVersion = isDefaultVersion;
		});
		this.log(updated);
		return updated;
	}

	public setVersionCount(versionCount: number): UserSettings {
		const updated = produce(this, (draft: UserSettings) => {
			draft.versionCount = versionCount;
		});
		this.log(updated);
		return updated;
	}

	public setSelectedContext(ctxIx: number): UserSettings {
		const updated = produce(this, (draft: UserSettings) => {
			draft.selectedContext = ctxIx;
		});
		this.log(updated);
		return updated;
	}

	public setIsJobsCacheActive(isActive: boolean): UserSettings {
		const updated = produce(this, (draft: UserSettings) => {
			draft.isJobsCacheActive = isActive;
		});
		this.log(updated);
		return updated;
	}

	public setProjectsViewMode(mode: string): UserSettings {
		const updated = produce(this, (draft: UserSettings) => {
			draft.projectsViewMode = mode;
		});
		this.log(updated);
		return updated;
	}

	public setProjectsSearchTerm(term: string): UserSettings {
		const updated = produce(this, (draft: UserSettings) => {
			draft.projectsSearchTerm = term;
		});
		this.log(updated);
		return updated;
	}

	public setSelectedElement(element: DcCoreElementType): UserSettings {
		const updated = produce(this, (draft: UserSettings) => {
			draft.selectedElement = element;
		});
		this.log(updated);
		return updated;
	}

	public setModule(module: DcCoreModule): UserSettings {
		const updated = produce(this, (draft: UserSettings) => {
			draft.module = module;
		});
		this.log(updated);
		return updated;
	}
}

@Injectable({
	providedIn: 'root',
})
export class ProjectStorage extends DcLocalStorage<IProjectStorage> {
	protected key = 'user';
	private settings;

	public constructor(@Inject(WINDOW) private readonly window: Window) {
		super();
		this.settings = new UserSettings();
		this.synchronize();
	}

	public setValue(val: IProjectStorage): void {
		const current = this.getValue();
		this.settings = this.settings
			.setProjectId(val.projectId)
			.setProjectLabel(val.projectLabel)
			.setProjectVersion(val.projectVersion)
			.setIsDefaultVersion(val.isDefaultVersion)
			.setVersionCount(val.versionCount);
		this.setStorage({
			...current,
			...val,
		});
	}

	public getValue(): IProjectStorage {
		return this.settings;
	}

	public getProjectId(): number {
		return this.settings.projectId;
	}

	public getProjectLabel(): string {
		return this.settings.projectLabel;
	}

	public getProjectVersion(): string {
		return this.settings.projectVersion;
	}

	public getProjectIsDefaultVersion(): boolean {
		return this.settings.isDefaultVersion;
	}

	public getProjectVersionCount(): number {
		return this.settings.versionCount;
	}

	public getSelectedContext(): number {
		return this.settings.selectedContext;
	}

	public isJobsCacheActive(): boolean {
		return this.settings.isJobsCacheActive;
	}

	public getProjectsViewMode(): string {
		return this.settings.projectsViewMode;
	}

	public getProjectsSearchTerm(): string {
		return this.settings.projectsSearchTerm;
	}

	public getSelectedElement(): DcCoreElementType {
		return this.settings.selectedElement;
	}

	public getModule(): DcCoreModule {
		return this.settings.module;
	}

	public setCurrentContext(ctx: number): void {
		this.settings = this.settings.setSelectedContext(ctx);
		this.setStorage(this.settings);
	}

	public setIsJobsCacheActive(isActive: boolean): void {
		this.settings = this.settings.setIsJobsCacheActive(isActive);
		this.setStorage(this.settings);
	}

	public setProjectsSearchTerm(term: string): void {
		this.settings = this.settings.setProjectsSearchTerm(term);
		this.setStorage(this.settings);
	}

	public setProjectsViewMode(mode: string): void {
		this.settings = this.settings.setProjectsViewMode(mode);
		this.setStorage(this.settings);
	}

	public setSelectedElement(element: DcCoreElementType): void {
		this.settings = this.settings.setSelectedElement(element);
		this.setStorage(this.settings);
	}

	public setModule(module: DcCoreModule): void {
		this.settings = this.settings.setModule(module);
		this.setStorage(this.settings);
	}

	private synchronize(): void {
		const stored = this.getStorage();
		if (stored) {
			this.setProjectsViewMode(
				stored.projectsViewMode ?? this.settings.projectsViewMode
			);
			this.setProjectsSearchTerm(stored.projectsSearchTerm);
			this.setSelectedElement(stored.selectedElement);
			this.setModule(stored.module);
		} else {
			this.setStorage(this.settings);
		}
	}
}
